// ============================================
// TASK-9390 — Job "Type": the tenant's managed list + ONE label resolver
// ============================================
// Side-effect module: attaches `window.JobTypes`. app.js imports it right after
// sharedFormat.js, before any view module, and views read it through
// `window.JobTypes` at CALL time — never a cross-`?v=` ES import (Pitfall #1).
//
// DATA MODEL
//   tenants/{tid}.settings.jobTypes = [{ value, label, archived? }]
//     value  — a stable slug, generated ONCE when the type is added. Jobs store
//              this in `job.type`. A rename changes `label` only, so no job doc
//              is ever rewritten by editing the list.
//     label  — what every surface shows (≤ 40 chars).
//     archived — hidden from the Add Job dropdown for NEW picks; a job that
//              already holds it still shows it. There is no hard delete.
//     rentalDirection? — TASK-10006 S2: 'out' (rentals on this type go out) |
//              'back' (rentals on this type come back) | absent = neither, which
//              is today's behaviour (every rental picked defaults to Deploy).
//              Only the two known words survive list(); anything else is absent.
//              Read by dispatch.js (Add/Edit Job default direction, the return
//              job's type) and mirrored by the server list twins (index.js
//              _driverJobTypeList, crewScheduleText.js jobTypeList).
//   Unset → DEFAULTS. The three default slugs ('job', 'shop_day', 'meeting') are
//   load-bearing: dispatch.js keys the Meeting / Shop Day card badges and the
//   dimmed-customer form state off them, so they are never renamed as slugs.
//
// LEGACY FREE TEXT (the field was an <input type="text"> until TASK-9390).
// Measured 2026-09-24: 1,100+ jobs hold 'job', ~370 hold free text ('Hydrovac ',
// 'Equipment move ', 'Safety Meeting', …), 58 hold ''. None of that is migrated.
// A value that is not in the list DISPLAYS AS ITSELF (trimmed), and the job
// form offers it as a one-off option so opening + saving an old job keeps it
// byte-for-byte (see dispatch.js _setJobTypeSelectValue).
//
// ESCAPING: everything here returns PLAIN TEXT. Callers escape at the sink.
(function () {
  'use strict';

  const LABEL_MAX = 40;
  const LIST_MAX = 100; // firestore.rules t9390JobTypesOk caps the list at the same size
  const HEADER_MAX = 100; // a legacy free-text type can be longer than a managed label

  const DEFAULTS = Object.freeze([
    Object.freeze({ value: 'job', label: 'Job' }),
    Object.freeze({ value: 'shop_day', label: 'Shop Day' }),
    Object.freeze({ value: 'meeting', label: 'Meeting' }),
  ]);
  const DEFAULT_LABEL = Object.freeze({ job: 'Job', shop_day: 'Shop Day', meeting: 'Meeting' });

  // TASK-10006 S2 — the two directions a type can set. Anything else = none.
  const RENTAL_DIRECTIONS = Object.freeze(['out', 'back']);
  function _dir(v) { return v === 'out' || v === 'back' ? v : null; }

  function _str(v) { return v == null ? '' : String(v); }
  function _key(v) { return _str(v).trim().toLowerCase(); }

  /** Clean a label for storage: collapse whitespace, trim, cap at LABEL_MAX. */
  function cleanLabel(raw) {
    return _str(raw).replace(/\s+/g, ' ').trim().slice(0, LABEL_MAX);
  }

  /**
   * The tenant's list, sanitised. Never throws, never returns an empty list.
   * Drops malformed rows and duplicate values (first wins); an absent / empty /
   * non-array setting is the DEFAULTS. The default 'job' slug is guaranteed
   * present (prepended if a console write dropped it) because the form and the
   * save path fall back to it.
   */
  function list(settings) {
    const raw = settings && Array.isArray(settings.jobTypes) ? settings.jobTypes : null;
    if (!raw || !raw.length) return DEFAULTS.map(t => ({ value: t.value, label: t.label, archived: false }));
    const seen = new Set();
    const out = [];
    for (const r of raw) {
      if (!r || typeof r !== 'object') continue;
      const value = _str(r.value).trim();
      if (!value || seen.has(value)) continue;
      const label = cleanLabel(r.label) || DEFAULT_LABEL[value] || value;
      seen.add(value);
      const row = { value, label, archived: r.archived === true };
      const dir = _dir(r.rentalDirection);
      if (dir) row.rentalDirection = dir; // absent = today's behaviour (no key, so old lists stay byte-identical)
      out.push(row);
      if (out.length >= LIST_MAX) break;
    }
    if (!seen.has('job')) out.unshift({ value: 'job', label: 'Job', archived: false });
    return out;
  }

  /** The list entry a stored job.type points at: exact value first, then case/space-folded. */
  function find(value, typeList) {
    const v = _str(value);
    if (!v.trim()) return null;
    const l = Array.isArray(typeList) ? typeList : DEFAULTS;
    const exact = l.find(t => t && t.value === v);
    if (exact) return exact;
    const k = _key(v);
    return l.find(t => t && _key(t.value) === k) || null;
  }

  /**
   * TASK-10006 S2 — the rental direction a stored `job.type` sets:
   * 'out' | 'back' | null. Looked up by the SLUG (find), never by the label, so
   * a renamed type keeps its direction and no name ("Removal", "Install") is
   * ever read. Legacy free text / unknown / default-unset → null.
   */
  function rentalDirection(value, typeList) {
    const hit = find(value, Array.isArray(typeList) ? typeList : DEFAULTS);
    return hit ? _dir(hit.rentalDirection) : null;
  }

  /**
   * TASK-10006 S2 — the type a RETURN job (Create return job / the save
   * prompt's return job) should carry: the current type when it already
   * brings rentals back, else the first ACTIVE 'back' type on the list, else
   * null (no type says "come back" → leave the copied type alone, as today).
   */
  function returnTypeFor(typeList, currentValue) {
    const l = Array.isArray(typeList) ? typeList : DEFAULTS;
    if (rentalDirection(currentValue, l) === 'back') return _str(currentValue);
    const hit = l.find(t => t && !t.archived && _dir(t.rentalDirection) === 'back');
    return hit ? hit.value : null;
  }

  /** True for the default type: unset, '', or 'job' in any case/spacing. */
  function isDefault(value) {
    const k = _key(value);
    return k === '' || k === 'job';
  }

  /**
   * Display label for a stored `job.type`.
   *   list hit  → its label (a rename shows everywhere, no job rewritten)
   *   '' / unset → the list's 'job' label ('Job')
   *   anything else (legacy free text) → the value itself, trimmed
   */
  function label(value, typeList) {
    const l = Array.isArray(typeList) ? typeList : DEFAULTS;
    if (!_str(value).trim()) {
      const d = find('job', l);
      return d ? d.label : 'Job';
    }
    const hit = find(value, l);
    if (hit) return hit.label;
    return _str(value).trim();
  }

  /**
   * Label for a JOB doc. Same as label(job.type), except a custom slug the list
   * no longer knows (malformed settings, another tenant's data) falls back to
   * the `typeLabel` saveJob stamps beside it before ever showing a raw slug.
   */
  function labelForJob(job, typeList) {
    if (!job) return '';
    const v = job.type;
    const l = Array.isArray(typeList) ? typeList : DEFAULTS;
    if (find(v, l) || !_str(v).trim()) return label(v, l);
    const stamped = cleanLabel(job.typeLabel);
    return stamped || label(v, l);
  }

  /**
   * A new stable slug for `rawLabel`, unique against `existingValues`.
   * Prefixed `ct_` so it can never collide with a default slug OR with legacy
   * free text a job already holds (no legacy value starts with `ct_` — measured),
   * which would otherwise silently re-label those old jobs.
   */
  function makeValue(rawLabel, existingValues) {
    const taken = new Set((existingValues || []).map(_str));
    let base = 'ct_' + (cleanLabel(rawLabel).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30) || 'type');
    let v = base;
    let n = 2;
    while (taken.has(v)) v = base + '_' + (n++);
    return v;
  }

  /**
   * Options for the Add/Edit Job <select>: active types, plus the CURRENT value
   * when it is archived or legacy free text (so it is never silently replaced).
   * Returns [{ value, label, archived?, custom? }] — plain text, caller escapes.
   */
  function options(typeList, currentValue) {
    const l = Array.isArray(typeList) ? typeList : list(null);
    const cur = _str(currentValue);
    const out = l.filter(t => !t.archived || t.value === cur).map(t => ({ value: t.value, label: t.label, archived: !!t.archived }));
    if (cur.trim() && !out.some(o => o.value === cur)) {
      out.push({ value: cur, label: cur.trim(), custom: true });
    }
    return out;
  }

  // Header-field toggles (AR/AP → Templates). settings.headerFields =
  // { jobType: { ticket: bool, invoice: bool } }, default off. Twin of
  // functions/src/services/fieldTicketDocument.js headerJobTypeFor — the client
  // renderers and the server PDF/email read the SAME predicate, run on one
  // fixture table by tests/task9390-job-type.unit.cjs.
  function headerOn(settings, field, surface) {
    const hf = settings && settings.headerFields;
    const f = hf && typeof hf === 'object' ? hf[field] : null;
    return !!(f && typeof f === 'object' && f[surface] === true);
  }
  /**
   * The Type value a ticket/invoice header prints ('' = omit). Reads the
   * ticket's OWN snapshot (`ticket.jobType`, stamped at ticket creation) and
   * nothing else — never the live job — so the printed ticket, the invoice,
   * the emailed PDF and the approval page can only ever agree.
   */
  function headerJobType(ticket, settings, surface) {
    if (!headerOn(settings, 'jobType', surface)) return '';
    return _str(ticket && ticket.jobType).replace(/\s+/g, ' ').trim().slice(0, HEADER_MAX);
  }

  window.JobTypes = Object.freeze({
    LABEL_MAX, LIST_MAX, HEADER_MAX, DEFAULTS, RENTAL_DIRECTIONS,
    cleanLabel, list, find, isDefault, label, labelForJob, makeValue, options,
    rentalDirection, returnTypeFor,
    headerOn, headerJobType,
  });
})();
